// src/app/components/common/Input.jsx
import React, { useState } from "react";

const Input = ({
  label,
  error,
  helperText,
  type = "text",
  icon = null,
  iconPosition = "left",
  size = "md",
  variant = "default",
  fullWidth = true,
  className = "",
  containerClassName = "",
  ...props 
}) => {
  const [showPassword, setShowPassword] = useState(false);
  
  const variants = {
    default: "bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 focus:border-primary-500 focus:ring-primary-500",
    filled: "bg-gray-50 dark:bg-gray-700 border-transparent focus:bg-white dark:focus:bg-gray-800 focus:border-primary-500 focus:ring-primary-500",
    flushed: "border-0 border-b-2 border-gray-200 dark:border-gray-700 bg-transparent px-0 rounded-none focus:border-primary-500 focus:ring-0",
    unstyled: "border-0 bg-transparent focus:ring-0",
  };
  
  const sizes = {
    sm: "px-3 py-1.5 text-sm",
    md: "px-4 py-2.5 text-base",
    lg: "px-5 py-3 text-lg",
  };
  
  const widthClass = fullWidth ? "w-full" : "";
  const hasIcon = icon || type === "password";
  const inputType = type === "password" && showPassword ? "text" : type;
  
  const renderIcon = () => {
    if (type === "password") {
      return (
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          tabIndex={-1}
        >
          {showPassword ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L6.59 6.59m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
            </svg>
          )}
        </button>
      );
    }
    
    if (icon) {
      return (
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          {typeof icon === "string" ? (
            <span className="material-icons text-gray-400">{icon}</span>
          ) : (
            React.cloneElement(icon, {
              className: `w-5 h-5 text-gray-400 ${icon.props.className || ""}`
            })
          )}
        </div>
      );
    }
    
    return null;
  };
  
  return (
    <div className={`flex flex-col gap-1.5 ${containerClassName}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
          {props.required && <span className="text-rose-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        {hasIcon && iconPosition === "left" && type !== "password" && renderIcon()}
        
        <input
          type={inputType}
          className={`
            ${sizes[size]}
            ${variants[variant]}
            ${widthClass}
            ${hasIcon && iconPosition === "left" && type !== "password" ? "pl-10" : ""}
            ${hasIcon && iconPosition === "right" && type !== "password" ? "pr-10" : ""}
            ${type === "password" ? "pr-10" : ""}
            border rounded-lg transition-all duration-200
            focus:ring-2 focus:ring-opacity-20 focus:outline-none
            placeholder:text-gray-400 dark:placeholder:text-gray-500
            disabled:opacity-50 disabled:cursor-not-allowed
            ${error ? "border-rose-500 focus:border-rose-500 focus:ring-rose-500" : ""}
            ${className}
          `}
          {...props}
        />
        
        {(hasIcon && iconPosition === "right" && type !== "password") && renderIcon()}
        {type === "password" && renderIcon()}
      </div>
      
      {(error || helperText) && (
        <div className="flex items-center gap-1">
          {error ? (
            <>
              <svg className="w-4 h-4 text-rose-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-sm text-rose-600 dark:text-rose-400">{error}</span>
            </>
          ) : (
            <span className="text-sm text-gray-500 dark:text-gray-400">{helperText}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default Input;